import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Alert,
  FormHelperText,
  Autocomplete,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { extractNoteFields } from '../../../api/note.api.js';

function NoteForm({
  note, onSave, onCancel, loading = false,
}) {
  const [formData, setFormData] = useState({
    contact: '',
    date: null,
    note: '',
    type: 'General',
    author: '',
    tags: [],
  });

  const [errors, setErrors] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [nlpProvider, setNlpProvider] = useState('openai');
  const [extracting, setExtracting] = useState(false);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (note) {
      setFormData({
        contact: note.contact || '',
        date: note.date ? new Date(note.date) : null,
        note: note.note || '',
        type: note.type || 'General',
        author: note.author || '',
        tags: note.tags || [],
      });
    }
  }, [note]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.note.trim()) {
      newErrors.note = 'Note content is required';
    }
    return newErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const noteData = {
      ...formData,
      date: formData.date ? formData.date.toISOString() : null,
    };

    onSave(noteData);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const startListening = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        setTranscript('');
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        setTranscript(finalTranscript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } else {
      alert('Speech recognition is not supported in this browser.');
    }
  };

  const stopListening = () => {
    if ('webkitSpeechRecognition' in window) {
      window.webkitSpeechRecognition.abort();
    }
  };

  const handleExtractFields = async () => {
    if (!transcript.trim()) {
      alert('Please provide some text to extract fields from.');
      return;
    }

    setExtracting(true);
    try {
      const result = await extractNoteFields(transcript, nlpProvider);
      if (result.success && result.fields) {
        const extractedData = { ...formData };

        if (result.fields.contact) extractedData.contact = result.fields.contact;
        if (result.fields.date) {
          const date = new Date(result.fields.date);
          if (!isNaN(date.getTime())) {
            extractedData.date = date;
          }
        }
        if (result.fields.type) extractedData.type = result.fields.type;
        if (result.fields.tags && Array.isArray(result.fields.tags)) {
          extractedData.tags = [...new Set([...formData.tags, ...result.fields.tags])];
        }
        if (result.fields.note) extractedData.note = result.fields.note;

        setFormData(extractedData);
        setTranscript('');
        alert('Fields extracted successfully!');
      }
    } catch (error) {
      console.error('Error extracting fields:', error);
      alert('Failed to extract fields. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <Stack spacing={3} sx={{ mt: 1 }}>
        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <DescriptionIcon color="primary" />
              <Typography variant="h6">
                {note ? 'Edit Note' : 'Add New Note'}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Note Content"
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                error={!!errors.note}
                helperText={errors.note}
                required
                placeholder="Enter your note content here..."
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Contact"
                  value={formData.contact}
                  onChange={(e) => handleInputChange('contact', e.target.value)}
                  placeholder="Related contact or person"
                />

                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="Call">Call</MenuItem>
                    <MenuItem value="Meeting">Meeting</MenuItem>
                    <MenuItem value="Email">Email</MenuItem>
                    <MenuItem value="FollowUp">Follow Up</MenuItem>
                    <MenuItem value="Research">Research</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Author"
                  value={formData.author}
                  onChange={(e) => handleInputChange('author', e.target.value)}
                  placeholder="Note author"
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Note Date"
                    value={formData.date}
                    onChange={(newValue) => handleInputChange('date', newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Tags */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <LabelIcon color="primary" />
              <Typography variant="h6">Tags & Categories</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Current Tags
                </Typography>
                <Box sx={{
                  display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2,
                }}
                >
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                    />
                  ))}
                  {formData.tags.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No tags added yet
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Add Tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Enter tag name"
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                  >
                    Add
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Voice Input & NLP */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <MicIcon color="primary" />
              <Typography variant="h6">Voice Input & AI Extraction</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Alert severity="info">
                Use voice input to quickly create notes. The AI will extract relevant fields from your speech.
              </Alert>

              <FormControl fullWidth>
                <InputLabel>NLP Provider</InputLabel>
                <Select
                  value={nlpProvider}
                  label="NLP Provider"
                  onChange={(e) => setNlpProvider(e.target.value)}
                >
                  <MenuItem value="openai">OpenAI GPT</MenuItem>
                  <MenuItem value="llama4">Llama 4 (Together AI)</MenuItem>
                  <MenuItem value="ollama">Ollama (Local)</MenuItem>
                </Select>
                <FormHelperText>
                  Choose the AI model for field extraction
                </FormHelperText>
              </FormControl>

              <Box>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <IconButton
                    onClick={isListening ? stopListening : startListening}
                    color={isListening ? 'error' : 'primary'}
                    size="large"
                  >
                    {isListening ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {isListening ? 'Listening... Click to stop' : 'Click to start voice input'}
                  </Typography>
                </Stack>

                {transcript && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Voice Transcript"
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}

                {transcript && (
                  <Button
                    variant="outlined"
                    onClick={handleExtractFields}
                    disabled={extracting}
                    startIcon={<DescriptionIcon />}
                  >
                    {extracting ? 'Extracting...' : 'Extract Fields'}
                  </Button>
                )}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3,
        }}
        >
          <Button onClick={onCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : (note ? 'Update' : 'Save')}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

export default NoteForm;
